// 자막 생성기 — 영상 업로드 → Gemini 자동 자막(진행률 %) → 영상 보며 편집 → SRT/VTT 다운로드.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../shared/api/client";
import { I } from "../../shared/Icon";
import { toast } from "../../shared/stores/toasts";
import { useAuth } from "../../shared/stores/auth";

// ── Types ─────────────────────────────────────────────────────────────────────

type Cue = { start: number; end: number; text: string };

type Job = {
  id: string;
  filename: string;
  size_bytes: number;
  status: "processing" | "done" | "error";
  progress: number;
  step: string;
  error: string | null;
  duration: number | null;
  cue_count: number;
  warning?: string | null;
  created_at: number;
  cues?: Cue[];
};

const MAX_BYTES = 2 * 1024 * 1024 * 1024;
const ALLOWED = /\.(mp4|mov|webm|mkv|avi|m4v)$/i;

// ── Time helpers ──────────────────────────────────────────────────────────────

function pad(n: number, w = 2) { return String(n).padStart(w, "0"); }

/** 초 → "mm:ss.d" (편집 UI 표기) */
function secLabel(s: number) {
  const m = Math.floor(s / 60), sec = s - m * 60;
  return `${pad(m)}:${sec.toFixed(1).padStart(4, "0")}`;
}
/** "mm:ss.d" | "hh:mm:ss" | 숫자초 → 초 */
function parseLabel(v: string): number | null {
  const t = v.trim();
  if (/^\d+(\.\d+)?$/.test(t)) return parseFloat(t);
  const parts = t.split(":").map(p => parseFloat(p));
  if (parts.some(isNaN)) return null;
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return null;
}
function srtTime(s: number) {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60), ms = Math.round((s - Math.floor(s)) * 1000);
  return `${pad(h)}:${pad(m)}:${pad(sec)},${pad(ms, 3)}`;
}
function vttTime(s: number) { return srtTime(s).replace(",", "."); }

function toSrt(cues: Cue[]) {
  return cues.map((c, i) => `${i + 1}\n${srtTime(c.start)} --> ${srtTime(c.end)}\n${c.text}`).join("\n\n") + "\n";
}
function toVtt(cues: Cue[]) {
  return "WEBVTT\n\n" + cues.map(c => `${vttTime(c.start)} --> ${vttTime(c.end)}\n${c.text}`).join("\n\n") + "\n";
}
function downloadText(name: string, content: string) {
  const url = URL.createObjectURL(new Blob([content], { type: "text/plain;charset=utf-8" }));
  const a = document.createElement("a"); a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}
function fmtSize(b: number) {
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

// ── Upload dropzone ───────────────────────────────────────────────────────────

function UploadZone({ onUpload, uploading, uploadPct }: {
  onUpload: (f: File) => void; uploading: boolean; uploadPct: number;
}) {
  const [over, setOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const pick = (f: File | undefined) => {
    if (!f) return;
    if (!ALLOWED.test(f.name)) { toast.error("지원하지 않는 형식", "mp4 · mov · webm · mkv · avi · m4v"); return; }
    if (f.size > MAX_BYTES) { toast.error("파일 크기 초과", "최대 2GB"); return; }
    onUpload(f);
  };

  return (
    <div
      onDragOver={e => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={e => { e.preventDefault(); setOver(false); pick(e.dataTransfer.files[0]); }}
      onClick={() => !uploading && inputRef.current?.click()}
      style={{
        border: `2px dashed ${over ? "var(--p-500, #6366f1)" : "var(--border)"}`,
        borderRadius: 14, padding: "44px 24px", textAlign: "center",
        cursor: uploading ? "default" : "pointer",
        background: over ? "rgba(99,102,241,0.06)" : "var(--bg-1, #f8f9fa)",
        transition: "border-color .15s, background .15s",
      }}>
      <input ref={inputRef} type="file" accept=".mp4,.mov,.webm,.mkv,.avi,.m4v,video/*" hidden
        onChange={e => { pick(e.target.files?.[0]); e.target.value = ""; }} />
      {uploading ? (
        <>
          <div style={{ fontSize: 34, marginBottom: 10 }}>⏫</div>
          <div style={{ fontWeight: 700, fontSize: 15, color: "var(--text-1)" }}>업로드 중… {uploadPct}%</div>
          <div style={{ width: 280, height: 8, borderRadius: 4, background: "var(--border)", margin: "12px auto 0" }}>
            <div style={{ width: `${uploadPct}%`, height: "100%", borderRadius: 4, background: "var(--p-500, #6366f1)", transition: "width .2s" }} />
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 34, marginBottom: 10 }}>🎬</div>
          <div style={{ fontWeight: 700, fontSize: 15, color: "var(--text-1)" }}>영상을 끌어다 놓거나 클릭해서 업로드</div>
          <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 6 }}>
            mp4 · mov · webm · mkv · avi · m4v — 최대 2GB<br />
            업로드하면 AI가 음성을 분석해 자막을 자동 생성합니다
          </div>
        </>
      )}
    </div>
  );
}

// ── Progress card ─────────────────────────────────────────────────────────────

function ProgressCard({ job }: { job: Job }) {
  return (
    <div className="t-card" style={{ padding: 24, maxWidth: 560, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <span style={{ fontSize: 22 }}>🎙️</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "var(--text-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{job.filename}</div>
          <div style={{ fontSize: 12, color: "var(--text-3)" }}>{job.step}</div>
        </div>
        <span style={{ fontWeight: 800, fontSize: 22, color: "var(--p-600, #4f46e5)" }} className="num">{job.progress}%</span>
      </div>
      <div style={{ height: 10, borderRadius: 5, background: "var(--border)", overflow: "hidden" }}>
        <div style={{
          width: `${job.progress}%`, height: "100%", borderRadius: 5,
          background: "linear-gradient(90deg, var(--p-400, #818cf8), var(--p-600, #4f46e5))",
          transition: "width .6s ease",
        }} />
      </div>
      <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 10, textAlign: "center" }}>
        영상 길이에 따라 수 분이 걸릴 수 있습니다 — 페이지를 떠나도 작업은 계속됩니다
      </div>
    </div>
  );
}

// ── Cue row ───────────────────────────────────────────────────────────────────

function CueRow({ cue, idx, active, onSeek, onChange, onDelete }: {
  cue: Cue; idx: number; active: boolean;
  onSeek: () => void;
  onChange: (c: Cue) => void;
  onDelete: () => void;
}) {
  const [startDraft, setStartDraft] = useState(secLabel(cue.start));
  const [endDraft, setEndDraft] = useState(secLabel(cue.end));
  useEffect(() => { setStartDraft(secLabel(cue.start)); }, [cue.start]);
  useEffect(() => { setEndDraft(secLabel(cue.end)); }, [cue.end]);

  const commitTime = (which: "start" | "end", draft: string) => {
    const v = parseLabel(draft);
    if (v == null) { which === "start" ? setStartDraft(secLabel(cue.start)) : setEndDraft(secLabel(cue.end)); return; }
    onChange({ ...cue, [which]: Math.max(0, Math.round(v * 1000) / 1000) });
  };

  const timeInput: React.CSSProperties = {
    width: 64, fontSize: 11.5, padding: "3px 6px", textAlign: "center",
    border: "1px solid var(--border)", borderRadius: 5,
    background: "var(--bg-0)", color: "var(--text-2)", fontFamily: "var(--font-mono, monospace)",
  };

  return (
    <div id={`cue-${idx}`}
      style={{
        display: "flex", gap: 8, padding: "8px 10px", borderRadius: 8,
        background: active ? "rgba(99,102,241,0.10)" : "transparent",
        border: `1px solid ${active ? "var(--p-400, #818cf8)" : "var(--border)"}`,
        marginBottom: 6, transition: "background .12s, border-color .12s",
      }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 4, flexShrink: 0, alignItems: "center" }}>
        <button onClick={onSeek} title="이 위치로 이동"
          style={{ fontSize: 11, fontWeight: 700, color: active ? "var(--p-600)" : "var(--text-3)", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
          #{idx + 1} ▶
        </button>
        <input value={startDraft} onChange={e => setStartDraft(e.target.value)}
          onBlur={() => commitTime("start", startDraft)}
          onKeyDown={e => { if (e.key === "Enter") commitTime("start", startDraft); }}
          style={timeInput} title="시작 (mm:ss.s)" />
        <input value={endDraft} onChange={e => setEndDraft(e.target.value)}
          onBlur={() => commitTime("end", endDraft)}
          onKeyDown={e => { if (e.key === "Enter") commitTime("end", endDraft); }}
          style={timeInput} title="끝 (mm:ss.s)" />
      </div>
      <textarea
        value={cue.text}
        onChange={e => onChange({ ...cue, text: e.target.value })}
        onFocus={onSeek}
        rows={2}
        style={{
          flex: 1, fontSize: 13, lineHeight: 1.5, padding: "6px 9px",
          border: "1px solid var(--border)", borderRadius: 6, resize: "vertical",
          background: "var(--bg-0)", color: "var(--text-1)", fontFamily: "inherit", minHeight: 48,
        }}
      />
      <button onClick={onDelete} title="이 자막 삭제"
        style={{ alignSelf: "flex-start", border: "none", background: "none", cursor: "pointer", color: "var(--text-3)", fontSize: 13, padding: "2px 4px" }}>
        ✕
      </button>
    </div>
  );
}

// ── Editor (video + cue list) ─────────────────────────────────────────────────

function Editor({ job, onBack, onDeleted }: { job: Job; onBack: () => void; onDeleted: () => void }) {
  const token = useAuth(s => s.accessToken);
  const [cues, setCues] = useState<Cue[]>(job.cues ?? []);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [now, setNow] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const followRef = useRef(true);

  const videoUrl = `${api.defaults.baseURL}/subtitle/jobs/${job.id}/video?token=${encodeURIComponent(token ?? "")}`;

  // 현재 시간에 해당하는 활성 큐
  const activeIdx = useMemo(() => cues.findIndex(c => now >= c.start && now < c.end), [cues, now]);

  // 활성 큐 자동 스크롤 (사용자가 직접 스크롤하면 일시 해제)
  useEffect(() => {
    if (activeIdx < 0 || !followRef.current) return;
    document.getElementById(`cue-${activeIdx}`)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [activeIdx]);

  const seek = (t: number) => {
    const v = videoRef.current;
    if (v) { v.currentTime = Math.max(0, t + 0.001); v.play().catch(() => {}); }
  };

  const updateCue = (idx: number, c: Cue) => {
    setCues(prev => prev.map((x, i) => i === idx ? c : x));
    setDirty(true);
  };
  const deleteCue = (idx: number) => {
    setCues(prev => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };
  const addCue = () => {
    const t = videoRef.current?.currentTime ?? 0;
    const c: Cue = { start: Math.round(t * 10) / 10, end: Math.round((t + 3) * 10) / 10, text: "" };
    setCues(prev => {
      const next = [...prev, c].sort((a, b) => a.start - b.start);
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const sorted = [...cues].sort((a, b) => a.start - b.start);
      await api.put(`/subtitle/jobs/${job.id}/cues`, { cues: sorted });
      setCues(sorted);
      setDirty(false);
      toast.success("자막 저장됨");
    } catch (e: any) {
      toast.error("저장 실패", e?.response?.data?.error?.message ?? "");
    } finally { setSaving(false); }
  };

  const removeJob = async () => {
    if (!confirm(`"${job.filename}" 작업과 영상·자막을 삭제하시겠습니까?`)) return;
    try { await api.delete(`/subtitle/jobs/${job.id}`); toast.success("삭제됨"); onDeleted(); }
    catch { toast.error("삭제 실패"); }
  };

  const base = (job.filename || "subtitle").replace(/\.[^.]+$/, "");
  const activeCue = activeIdx >= 0 ? cues[activeIdx] : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", borderBottom: "1px solid var(--border)", flexShrink: 0, flexWrap: "wrap" }}>
        <button className="t-btn" onClick={onBack} style={{ fontSize: 12 }}>← 목록</button>
        <span style={{ fontWeight: 700, fontSize: 14, color: "var(--text-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 320 }} title={job.filename}>
          {job.filename}
        </span>
        <span style={{ fontSize: 11, color: "var(--text-3)" }}>{cues.length}개 자막{job.warning ? ` · ⚠ ${job.warning}` : ""}</span>
        <div style={{ flex: 1 }} />
        <button className="t-btn" onClick={addCue} style={{ fontSize: 12 }}><I.Plus size={12} /> 현재 위치에 자막</button>
        <button className="t-btn t-btn-primary" onClick={save} disabled={saving || !dirty} style={{ fontSize: 12 }}>
          {saving ? "저장 중…" : dirty ? "저장" : "저장됨"}
        </button>
        <button className="t-btn" onClick={() => downloadText(`${base}.srt`, toSrt(cues))} style={{ fontSize: 12 }}>⬇ SRT</button>
        <button className="t-btn" onClick={() => downloadText(`${base}.vtt`, toVtt(cues))} style={{ fontSize: 12 }}>⬇ VTT</button>
        <button className="t-btn" onClick={removeJob} style={{ fontSize: 12, color: "#ef4444" }}>삭제</button>
      </div>

      {/* Body: video + cue list */}
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        {/* Video pane */}
        <div style={{ flex: "1 1 58%", minWidth: 0, display: "flex", flexDirection: "column", background: "#0b0b0d", position: "relative" }}>
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 0, position: "relative" }}>
            <video
              ref={videoRef}
              src={videoUrl}
              controls
              onTimeUpdate={e => setNow(e.currentTarget.currentTime)}
              style={{ maxWidth: "100%", maxHeight: "100%", width: "100%", outline: "none" }}
            />
            {/* 자막 오버레이 — 편집 내용이 즉시 반영됨 */}
            {activeCue?.text && (
              <div style={{
                position: "absolute", left: "50%", bottom: 64, transform: "translateX(-50%)",
                maxWidth: "86%", padding: "8px 16px", borderRadius: 8,
                background: "rgba(0,0,0,0.72)", color: "#fff",
                fontSize: 19, fontWeight: 600, lineHeight: 1.45, textAlign: "center",
                whiteSpace: "pre-wrap", pointerEvents: "none",
                textShadow: "0 1px 3px rgba(0,0,0,0.9)",
              }}>
                {activeCue.text}
              </div>
            )}
          </div>
        </div>

        {/* Cue list pane */}
        <div style={{ flex: "1 1 42%", minWidth: 320, maxWidth: 560, borderLeft: "1px solid var(--border)", display: "flex", flexDirection: "column", minHeight: 0 }}>
          <div style={{ display: "flex", alignItems: "center", padding: "8px 12px", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text-2)", flex: 1 }}>자막 편집</span>
            <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "var(--text-3)", cursor: "pointer" }}>
              <input type="checkbox" defaultChecked onChange={e => { followRef.current = e.target.checked; }} style={{ accentColor: "var(--p-500)" }} />
              재생 위치 따라가기
            </label>
          </div>
          <div ref={listRef} style={{ flex: 1, overflowY: "auto", padding: "8px 10px" }}>
            {cues.length === 0 && (
              <div style={{ textAlign: "center", color: "var(--text-3)", fontSize: 13, padding: "36px 0", lineHeight: 1.7 }}>
                자막이 없습니다.<br />음성이 없는 영상이거나 전사에 실패했습니다.<br />「현재 위치에 자막」으로 직접 추가할 수 있습니다.
              </div>
            )}
            {cues.map((c, i) => (
              <CueRow key={i} cue={c} idx={i} active={i === activeIdx}
                onSeek={() => seek(c.start)}
                onChange={next => updateCue(i, next)}
                onDelete={() => deleteCue(i)} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function SubtitlePage() {
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [current, setCurrent] = useState<Job | null>(null);   // processing 또는 done(편집 중)
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);

  const loadJobs = useCallback(() => {
    api.get<Job[]>("/subtitle/jobs").then(r => setJobs(r.data)).catch(() => setJobs([]));
  }, []);
  useEffect(() => { loadJobs(); }, [loadJobs]);

  // 진행 중 작업 폴링 (1.5초)
  useEffect(() => {
    if (!current || current.status !== "processing") return;
    const t = setInterval(async () => {
      try {
        const r = await api.get<Job>(`/subtitle/jobs/${current.id}`);
        setCurrent(r.data);
        if (r.data.status !== "processing") loadJobs();
      } catch { /* 다음 주기에 재시도 */ }
    }, 1500);
    return () => clearInterval(t);
  }, [current?.id, current?.status, loadJobs]);

  const upload = async (file: File) => {
    setUploading(true); setUploadPct(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post<{ job_id: string }>("/subtitle/jobs", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 0,
        onUploadProgress: ev => setUploadPct(ev.total ? Math.round((ev.loaded / ev.total) * 100) : 0),
      });
      const job = await api.get<Job>(`/subtitle/jobs/${r.data.job_id}`);
      setCurrent(job.data);
      loadJobs();
    } catch (e: any) {
      toast.error("업로드 실패", e?.response?.data?.error?.message ?? e?.message ?? "");
    } finally { setUploading(false); }
  };

  const openJob = async (j: Job) => {
    try {
      const r = await api.get<Job>(`/subtitle/jobs/${j.id}`);
      setCurrent(r.data);
    } catch { toast.error("작업을 불러올 수 없습니다"); }
  };

  // ── 편집 화면 ──────────────────────────────────────────────────────────────
  if (current?.status === "done") {
    return <Editor key={current.id} job={current} onBack={() => { setCurrent(null); loadJobs(); }} onDeleted={() => { setCurrent(null); loadJobs(); }} />;
  }

  // ── 업로드 / 진행 / 목록 화면 ──────────────────────────────────────────────
  return (
    <div style={{ height: "100%", overflowY: "auto" }}>
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "28px 20px 48px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
          <span style={{ fontSize: 26 }}>🎬</span>
          <div>
            <h2 style={{ margin: 0, fontSize: 19, fontWeight: 800, color: "var(--text-1)" }}>자막 생성기</h2>
            <p style={{ margin: "2px 0 0", fontSize: 12.5, color: "var(--text-3)" }}>영상을 올리면 AI가 자막을 만들고, 영상을 보면서 바로 수정할 수 있습니다</p>
          </div>
        </div>

        {current?.status === "processing" ? (
          <ProgressCard job={current} />
        ) : (
          <UploadZone onUpload={upload} uploading={uploading} uploadPct={uploadPct} />
        )}

        {current?.status === "error" && (
          <div className="t-card" style={{ padding: 16, marginTop: 14, borderLeft: "3px solid #ef4444" }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#ef4444", marginBottom: 4 }}>자막 생성 실패 — {current.filename}</div>
            <div style={{ fontSize: 12.5, color: "var(--text-2)" }}>{current.error}</div>
            <button className="t-btn" style={{ marginTop: 10, fontSize: 12 }} onClick={() => setCurrent(null)}>닫기</button>
          </div>
        )}

        {/* 이전 작업 */}
        <div style={{ marginTop: 28 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>내 작업</div>
          {jobs === null && <div style={{ fontSize: 13, color: "var(--text-3)", padding: "16px 0" }}>불러오는 중…</div>}
          {jobs?.length === 0 && <div style={{ fontSize: 13, color: "var(--text-3)", padding: "16px 0" }}>아직 작업이 없습니다.</div>}
          {jobs?.map(j => (
            <div key={j.id} className="t-card"
              onClick={() => openJob(j)}
              style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", marginBottom: 8, cursor: "pointer" }}>
              <span style={{ fontSize: 20, flexShrink: 0 }}>
                {j.status === "done" ? "✅" : j.status === "error" ? "⚠️" : "⏳"}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{j.filename}</div>
                <div style={{ fontSize: 11.5, color: "var(--text-3)" }}>
                  {fmtSize(j.size_bytes)}
                  {j.duration ? ` · ${Math.floor(j.duration / 60)}분 ${Math.round(j.duration % 60)}초` : ""}
                  {j.status === "done" ? ` · 자막 ${j.cue_count}개` : j.status === "processing" ? ` · ${j.step} ${j.progress}%` : ` · ${j.error?.slice(0, 60) ?? "실패"}`}
                </div>
              </div>
              {j.status === "processing" && (
                <span className="num" style={{ fontWeight: 800, fontSize: 15, color: "var(--p-600, #4f46e5)", flexShrink: 0 }}>{j.progress}%</span>
              )}
              <button
                onClick={async e => {
                  e.stopPropagation();
                  if (!confirm(`"${j.filename}" 작업을 삭제하시겠습니까?`)) return;
                  try { await api.delete(`/subtitle/jobs/${j.id}`); loadJobs(); if (current?.id === j.id) setCurrent(null); }
                  catch { toast.error("삭제 실패"); }
                }}
                title="삭제"
                style={{ border: "none", background: "none", cursor: "pointer", color: "var(--text-3)", fontSize: 13, flexShrink: 0 }}>
                🗑
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
